import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Clock, Users, User, Settings, PlayCircle, PauseCircle } from "lucide-react";
import { ActivityStudentsList } from "./ActivityStudentsList";
import { ActivitySettings } from "./ActivitySettings";

interface Activity {
  id: number;
  name: string;
  time: string;
  instructor: string;
  maxStudents: number;
  currentStudents: number;
  status: "active" | "upcoming" | "completed";
}

interface ActivityCardProps {
  activity: Activity;
  showActions?: boolean;
  onUpdate?: () => void;
}

export const ActivityCard = ({ activity, showActions = false, onUpdate }: ActivityCardProps) => {
  const [showStudentsList, setShowStudentsList] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const getStatusBadge = () => {
    switch (activity.status) {
      case "active":
        return (
          <Badge className="bg-gradient-success">
            <PlayCircle className="w-3 h-3 mr-1" />
            Activa
          </Badge>
        );
      case "upcoming":
        return (
          <Badge variant="outline">
            <Clock className="w-3 h-3 mr-1" />
            Próxima
          </Badge>
        );
      case "completed":
        return (
          <Badge variant="secondary">
            <PauseCircle className="w-3 h-3 mr-1" />
            Finalizada
          </Badge>
        );
      default:
        return null;
    }
  };

  const getOccupancyColor = () => {
    if (activity.currentStudents === 0) return "text-muted-foreground";
    const percentage = (activity.currentStudents / activity.maxStudents) * 100;
    if (percentage >= 90) return "text-destructive";
    if (percentage >= 75) return "text-orange-500";
    return "text-success";
  };

  return (
    <Card className="shadow-card hover:shadow-lg transition-smooth">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg">{activity.name}</CardTitle>
            <div className="flex items-center text-sm text-muted-foreground mt-1">
              <Clock className="w-4 h-4 mr-1" />
              {activity.time}
            </div>
          </div>
          {getStatusBadge()}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Instructor */}
        <div className="flex items-center">
          <User className="w-4 h-4 mr-2 text-primary" />
          <span className="text-sm">{activity.instructor}</span>
        </div>

        {/* Ocupación */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center">
              <Users className="w-4 h-4 mr-2 text-primary" />
              <span>Alumnos</span>
            </div>
            <span className={`font-medium ${getOccupancyColor()}`}>
              {activity.currentStudents}/{activity.maxStudents}
            </span>
          </div>
          
          {/* Barra de progreso */}
          <div className="w-full bg-muted rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-smooth ${
                (activity.currentStudents / activity.maxStudents) * 100 >= 90
                  ? "bg-destructive"
                  : (activity.currentStudents / activity.maxStudents) * 100 >= 75
                  ? "bg-orange-500"
                  : "bg-gradient-success"
              }`}
              style={{
                width: `${Math.min((activity.currentStudents / activity.maxStudents) * 100, 100)}%`
              }}
            />
          </div>
        </div>

        {/* Acciones (si se muestran) */}
        {showActions && (
          <div className="flex gap-2 pt-2">
            <Button 
              size="sm" 
              variant="outline" 
              className="flex-1"
              onClick={() => setShowStudentsList(true)}
            >
              <Users className="w-4 h-4 mr-1" />
              Ver Alumnos
            </Button>
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => setShowSettings(true)}
            >
              <Settings className="w-4 h-4" />
            </Button>
          </div>
        )}

        {/* Modales */}
        <ActivityStudentsList
          activity={activity}
          open={showStudentsList}
          onOpenChange={setShowStudentsList}
        />
        
        <ActivitySettings
          activity={activity}
          open={showSettings}
          onOpenChange={setShowSettings}
          onUpdate={() => {
            onUpdate?.();
            setShowSettings(false);
          }}
        />
      </CardContent>
    </Card>
  );
};